import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAddAccountHeadMutation } from "../../services/accountHeadApi";
import SelectAChead from "../Common/CustomSelect/SelectAChead";
import { notify } from "../Utility/Notify";
import axios from "axios";
import SideBar from "../Common/SideBar/SideBar";
import Header from "../Common/Header/Header";
import LoadingModal from "../Common/Modal/LoadingModal";
import { signInUser } from "../Utility/Auth";

const AddAccountHead = () => {
  const auth = signInUser();
  const { aamarId } = auth || {};
  const BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001/api").replace(/\/+$/, "");
  const { register, handleSubmit, reset, setValue } = useForm({});
  const navigate = useNavigate();
  const [mc, setMc] = useState(null); // Master account selected
  const [accountCode, setAccountCode] = useState(""); // Account code input
  const [codeUnique, setCodeUnique] = useState(false); // Uniqueness check
  const [uniqueCode, setUniqueCode] = useState(""); // Uniqueness check
  const [loader, setLoader] = useState(false);
  const [addACHead] = useAddAccountHeadMutation();
  const handleLoaderClose = () => setLoader(false);

  // Function to check if the account code is unique
  const checkCodeInAPI = async (code) => {
    try {
      const cancelToken = axios.CancelToken.source();
      const response = await axios.get(
        `${BASE_URL}/accounthead/unique/${code}/${aamarId}`,
        {
          cancelToken: cancelToken.token
        }
      );

      if (response.data) {
        setCodeUnique(response.data.exists); // Code exists in the database
      }
    } catch (error) {
      console.error("Error checking code in API:", error);
      setCodeUnique(false); // Assume not unique on error
    }
  };

  // Check the number of SUB Account Head
  const checkCount = async (mcId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/accounthead/aamarAH/${mcId}/${aamarId}`
      );
      return response?.data?.exists ?? 0;
    } catch (error) {
      console.error("Error checking count in API:", error);
      return 0;
    }
  };

  const handleCodeChange = async (e) => {
    const code = e.target.value;
    setUniqueCode(code); // Update the account code state
    setValue("code", code, { shouldValidate: true });

    if (code !== "") {
      await checkCodeInAPI(code);
    } else {
      setCodeUnique(false);
    }
  };

  // Submit Create Account Head
  const handleAddAccountHead = async (data) => {
    const headCode = uniqueCode || data.code;
    let newData = {};
    if (!mc || mc.option === "no-master") {
      newData = { ...data, code: headCode, aamarId };
    } else {
      newData = { ...data, code: headCode, maId: mc.option, aamarId };
    }

    setLoader(true);
    try {
      await addACHead(newData).unwrap();
      notify("AccountHead created Successfully", "success");
      navigate("/accountheads");
    } catch (err) {
      console.error(err);
      notify("Error creating AccountHead", "error");
    } finally {
      setLoader(false);
    }
  };

  // Master Account Head Select
  const handleOnchange = async (e) => {
    if (e && e.option && e.option !== "no-master") {
      setMc(e); // Set the selected master account
      setAccountCode(e.code); // Set the base account code
      setUniqueCode(""); // Clear previous unique code
  
      try {
        // Generate the unique code based on sub-account count
        const count = await checkCount(e.option);
        const base = parseInt(e.code, 10) || 0;
        const cnt = parseInt(count, 10) || 0;
        const newCode = (base + cnt + 1).toString();
  
        // Update `uniqueCode` state and `AccountHead Code` field in the form
        setUniqueCode(newCode);
        setValue("code", newCode, { shouldValidate: true });
        await checkCodeInAPI(newCode);
      } catch (error) {
        console.error("Error generating unique code:", error);
      }
    } else {
      setAccountCode(""); // Reset account code
      setMc(null);
      setUniqueCode("");
      setValue("code", ""); // Clear the form field value
      setCodeUnique(false);
    }
  };

  console.log("Account Code", uniqueCode);
  return (
    <div>
      <LoadingModal
        title={"Please Wait"}
        onShow={loader}
        handleClose={handleLoaderClose}
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar />
          </div>
          <div className="col-md-10">
            <Header title="Add Account Head" />
            <div className="row mt-3">
              <div className="col-md-6 offset-md-3">
                <Form onSubmit={handleSubmit(handleAddAccountHead)}>
                  <Form.Group className="mb-3" controlId="formBasicName">
                    <Form.Label>AccountHead Name</Form.Label>
                    <Form.Control
                      {...register("name", { required: true })}
                      type="text"
                      placeholder="AccountHead Name"
                    />
                  </Form.Group>

                  <Form.Group
                    className="mb-3"
                    controlId="formBasicMasterAccount"
                  >
                    <Form.Label>Master AccountHead</Form.Label>
                    <SelectAChead mc={mc} handleOnchange={handleOnchange} />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicDescription">
                    <Form.Label>AccountHead Description</Form.Label>
                    <textarea
                      className="form-control"
                      placeholder="Write a description..."
                      {...register("description", {
                        required: true,
                        maxLength: 1000
                      })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicCode">
                    <Form.Label>AccountHead Code</Form.Label>
                    <Form.Control
                      {...register("code", { required: true })}
                      // disabled={accountCode !== "" && true}
                      type="number"
                      value={uniqueCode} // Bind the account code value
                      placeholder="Enter AccountHead Code"
                      onChange={handleCodeChange} // Validate code dynamically
                      isInvalid={codeUnique} // Highlight if code is not unique
                    />
                    {codeUnique && (
                      <Form.Control.Feedback type="invalid">
                        This code already exists. Please use a different code.
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Select {...register("status", { required: true })}>
                      <option value="active">Active</option>
                      <option value="suspend">Suspend</option>
                    </Form.Select>
                  </Form.Group>

                  <Button variant="dark" type="submit">
                    Add AccountHead
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAccountHead;
